import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, withModifiers as _withModifiers, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "food-stock-manage" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { key: 5 }
const _hoisted_7 = { class: "pagination-container" }
const _hoisted_8 = { class: "mb-3 text-sm text-gray-600" }
const _hoisted_9 = { class: "m-0 pl-5 max-h-40 overflow-y-auto" }

import { nextTick, onMounted, reactive, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    ArrowLeftOutlined,
    DownloadOutlined,
    ReloadOutlined,
    SearchOutlined,
    UploadOutlined,
  } from '@ant-design/icons-vue';
  import { message } from 'ant-design-vue';
  import zhCN from 'ant-design-vue/es/locale/zh_CN';
  import dayjs, { type Dayjs } from 'dayjs';
  import * as XLSX from 'xlsx';
  import type { FoodStockItemModel } from '@/model/food';
  import { fetchCategoryItemList, fetchCategoryList, initializeFoodStock } from '@/service/food';
  import type { FoodStockImportItemModel } from '@/service/food';
  import { fetchShopList } from '@/service/base';

  interface ShopOption {
    shopId?: number;
    shopName?: string;
  }

  interface CategoryOption {
    shopFoodCategoryId?: number;
    shopId?: number;
    categoryName?: string;
  }

  interface QueryData {
    shopId?: number;
    shopFoodCategoryId?: number;
    shopFoodName: string;
    shopFoodStatus?: number;
    searchField: string;
  }

  interface FoodQueryRequest {
    pageNum: number;
    pageSize: number;
    data: Partial<QueryData>;
    createtimeBegin?: string;
    createtimeEnd?: string;
  }

  interface ImportExcelRow {
    菜品ID?: unknown;
    库存数量?: unknown;
  }

  const SELECTED_SHOP_KEY = 'table_manage_selected_shop';

  
export default /*@__PURE__*/_defineComponent({
  __name: 'FoodStockManageView',
  setup(__props) {

  const columns = [
    {
      title: '菜品ID',
      dataIndex: 'foodId',
      key: 'foodId',
      width: 100,
    },
    {
      title: '店铺菜品ID',
      dataIndex: 'shopFoodId',
      key: 'shopFoodId',
      width: 90,
    },
    {
      title: '菜品名称',
      dataIndex: 'shopFoodName',
      key: 'shopFoodName',
      width: 180,
    },
    {
      title: '所属店铺',
      dataIndex: 'shopId',
      key: 'shopName',
      width: 160,
    },
    {
      title: '所属分类',
      dataIndex: 'shopFoodCategoryId',
      key: 'categoryName',
      width: 160,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 180,
    },
    {
      title: '当前库存',
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      width: 110,
      sorter: (a: FoodStockItemModel, b: FoodStockItemModel) =>
        (a.stockQuantity ?? 0) - (b.stockQuantity ?? 0),
    },
    {
      title: '状态',
      dataIndex: 'shopFoodStatus',
      key: 'shopFoodStatus',
      width: 100,
    },
    {
      title: '更新时间',
      dataIndex: 'modifyTime',
      key: 'modifyTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
    },
  ];

  const importColumns = [
    { title: '菜品ID', dataIndex: 'foodId', key: 'foodId' },
    { title: '库存数量', dataIndex: 'stockQuantity', key: 'stockQuantity' },
  ];

  const router = useRouter();
  const loading = ref(false);
  const exporting = ref(false);
  const stockSubmitting = ref(false);
  const stockModalVisible = ref(false);
  const importModalVisible = ref(false);
  const importSubmitting = ref(false);
  const importFileInput = ref<HTMLInputElement | null>(null);
  const importFileName = ref('');
  const importRows = ref<FoodStockImportItemModel[]>([]);
  const importErrors = ref<string[]>([]);
  const currentStockFood = ref<FoodStockItemModel | null>(null);
  const shopList = ref<ShopOption[]>([]);
  const categoryList = ref<CategoryOption[]>([]);
  const searchCategoryOptions = ref<CategoryOption[]>([]);
  const tableData = ref<FoodStockItemModel[]>([]);
  const total = ref(0);
  const dateRange = ref<[Dayjs, Dayjs] | null>(null);
  const stockForm = reactive({
    stockQuantity: undefined as number | undefined,
  });

  const queryParams = reactive({
    createtimeBegin: '',
    createtimeEnd: '',
    pageNum: 1,
    pageSize: 20,
    data: {
      shopId: undefined,
      shopFoodCategoryId: undefined,
      shopFoodName: '',
      shopFoodStatus: undefined,
      searchField: '',
    } as QueryData,
  });

  const resetImportState = () => {
    importFileName.value = '';
    importRows.value = [];
    importErrors.value = [];
    if (importFileInput.value) importFileInput.value.value = '';
  };

  const openImportFilePicker = () => {
    importFileInput.value?.click();
  };

  const validateImportRows = (
    rows: ImportExcelRow[],
    headers: string[],
  ): FoodStockImportItemModel[] => {
    const errors: string[] = [];
    const result: FoodStockImportItemModel[] = [];
    const foodIds = new Set<number>();

    if (!headers.includes('菜品ID')) errors.push('缺少必需列“菜品ID”');
    if (!headers.includes('库存数量')) errors.push('缺少必需列“库存数量”');
    if (errors.length > 0) {
      importErrors.value = errors;
      return result;
    }

    rows.forEach((row, index) => {
      const excelRow = index + 2;
      const rawFoodId = row['菜品ID'];
      const rawStockQuantity = row['库存数量'];
      const isBlank =
        (rawFoodId === undefined || rawFoodId === null || rawFoodId === '') &&
        (rawStockQuantity === undefined || rawStockQuantity === null || rawStockQuantity === '');
      if (isBlank) return;

      const foodId = Number(rawFoodId);
      const stockQuantity = Number(rawStockQuantity);
      let valid = true;
      if (!Number.isInteger(foodId) || foodId <= 0) {
        errors.push(`第 ${excelRow} 行：菜品ID必须为正整数`);
        valid = false;
      } else if (foodIds.has(foodId)) {
        errors.push(`第 ${excelRow} 行：菜品ID ${foodId} 重复`);
        valid = false;
      }
      if (!Number.isInteger(stockQuantity) || stockQuantity < 0) {
        errors.push(`第 ${excelRow} 行：库存数量必须为非负整数`);
        valid = false;
      }
      if (valid) {
        foodIds.add(foodId);
        result.push({ foodId, stockQuantity });
      }
    });

    if (rows.length === 0 || (result.length === 0 && errors.length === 0)) {
      errors.push('Excel 中没有可导入的数据');
    }
    importErrors.value = errors;
    return result;
  };

  const handleImportFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    resetImportState();
    importFileName.value = file.name;
    importModalVisible.value = true;
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        importErrors.value = ['Excel 中没有工作表'];
        return;
      }
      const sheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json<ImportExcelRow>(sheet, { defval: '' });
      const headerRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, range: 0 });
      const headers = (headerRows[0] || []).map((header) => String(header));
      importRows.value = validateImportRows(rawRows, headers);
    } catch (error) {
      console.error('解析库存 Excel 失败:', error);
      importErrors.value = ['文件无法解析，请确认文件是有效的 Excel 格式'];
    }
  };

  const handleImportCancel = () => {
    importModalVisible.value = false;
    resetImportState();
  };

  const handleImportConfirm = async () => {
    if (importErrors.value.length > 0 || importRows.value.length === 0) return;
    try {
      importSubmitting.value = true;
      const response = await initializeFoodStock({ stockList: importRows.value });
      if (response.code !== 200) {
        message.error(response.msg || '库存导入失败');
        return;
      }
      message.success(`成功导入 ${importRows.value.length} 条库存数据`);
      importModalVisible.value = false;
      resetImportState();
      await loadFoodList();
    } catch (error) {
      console.error('库存导入失败:', error);
      message.error('库存导入失败，请稍后重试');
    } finally {
      importSubmitting.value = false;
    }
  };

  const saveSelectedShop = (shopId: number | undefined) => {
    try {
      if (shopId) {
        localStorage.setItem(SELECTED_SHOP_KEY, String(shopId));
      } else {
        localStorage.removeItem(SELECTED_SHOP_KEY);
      }
    } catch (error) {
      console.warn('保存选中店铺失败:', error);
    }
  };

  const loadSelectedShop = () => {
    try {
      const savedShopId = localStorage.getItem(SELECTED_SHOP_KEY);
      return savedShopId ? Number(savedShopId) : undefined;
    } catch (error) {
      console.warn('读取选中店铺失败:', error);
      return undefined;
    }
  };

  const applySavedShopToQuery = async () => {
    const savedShopId = loadSelectedShop();
    const selected =
      shopList.value.find((shop) => Number(shop.shopId) === savedShopId) || shopList.value[0];
    queryParams.data.shopId = selected ? Number(selected.shopId) : undefined;
    if (!queryParams.data.shopId) return;
    saveSelectedShop(queryParams.data.shopId);
    queryParams.data.shopFoodCategoryId = undefined;
    await loadCategoryListByShop(queryParams.data.shopId);
  };

  const formatDateTime = (dateStr: string | Date | undefined) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss');
  };

  const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates[0] && dates[1]) {
      queryParams.createtimeBegin = dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
      queryParams.createtimeEnd = dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
    } else {
      queryParams.createtimeBegin = '';
      queryParams.createtimeEnd = '';
    }
  };

  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 999,
        data: { shopStatus: 0 },
      });
      if (response.code === 200) {
        shopList.value = (response.result.list || []) as ShopOption[];
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
      message.error('加载店铺列表失败');
    }
  };

  const loadCategoryList = async () => {
    try {
      const response = await fetchCategoryList({
        pageNum: 1,
        pageSize: 999,
        data: { categoryStatus: 0 },
      });
      if (response.code === 200) {
        categoryList.value = (response.result.list || []) as CategoryOption[];
      }
    } catch (error) {
      console.error('加载分类列表失败:', error);
    }
  };

  const loadCategoryListByShop = async (shopId: number) => {
    try {
      const response = await fetchCategoryList({
        pageNum: 1,
        pageSize: 999,
        data: { categoryStatus: 0, shopId },
      });
      if (response.code === 200) {
        searchCategoryOptions.value = (response.result.list || []) as CategoryOption[];
      } else {
        searchCategoryOptions.value = [];
      }
    } catch (error) {
      console.error('按店铺加载分类失败:', error);
      searchCategoryOptions.value = [];
    }
  };

  const loadFoodList = async () => {
    if (!queryParams.data.shopId) {
      tableData.value = [];
      total.value = 0;
      message.warning('请选择餐厅');
      return;
    }
    try {
      loading.value = true;
      const response = await fetchCategoryItemList(
        buildFoodQueryRequest(queryParams.pageNum, queryParams.pageSize),
      );
      if (response.code === 200) {
        tableData.value = (response.result.list || []) as FoodStockItemModel[];
        total.value = response.result.total || 0;
      } else {
        message.error(response.msg || '查询失败');
        tableData.value = [];
        total.value = 0;
      }
    } catch (error) {
      console.error('加载菜品库存列表失败:', error);
      message.error('加载菜品库存列表失败，请稍后重试');
      tableData.value = [];
      total.value = 0;
    } finally {
      loading.value = false;
    }
  };

  const buildFoodQueryRequest = (pageNum: number, pageSize: number): FoodQueryRequest => {
    const params: FoodQueryRequest = {
      pageNum,
      pageSize,
      data: {} as Partial<QueryData>,
      createtimeBegin: queryParams.createtimeBegin || undefined,
      createtimeEnd: queryParams.createtimeEnd || undefined,
    };

    if (queryParams.data.shopId !== undefined && queryParams.data.shopId !== null) {
      params.data.shopId = queryParams.data.shopId;
    }
    if (
      queryParams.data.shopFoodCategoryId !== undefined &&
      queryParams.data.shopFoodCategoryId !== null
    ) {
      params.data.shopFoodCategoryId = queryParams.data.shopFoodCategoryId;
    }
    if (queryParams.data.shopFoodName) {
      params.data.shopFoodName = queryParams.data.shopFoodName;
    }
    if (queryParams.data.shopFoodStatus !== undefined && queryParams.data.shopFoodStatus !== null) {
      params.data.shopFoodStatus = queryParams.data.shopFoodStatus;
    }
    if (queryParams.data.searchField) {
      params.data.searchField = queryParams.data.searchField;
    }

    return params;
  };

  const handleExport = async () => {
    try {
      exporting.value = true;
      const response = await fetchCategoryItemList(buildFoodQueryRequest(1, 1000));
      if (response.code !== 200) {
        message.error(response.msg || '导出数据查询失败');
        return;
      }
      const rows = (response.result.list || []) as FoodStockItemModel[];
      if (rows.length === 0) {
        message.warning('当前筛选条件下没有可导出的数据');
        return;
      }
      const exportRows = rows.map((food) => ({
        主菜品ID: food.foodId ?? '',
        店铺菜品ID: food.shopFoodId ?? '',
        菜品名称: food.shopFoodName || food.foodName || '',
        所属店铺: getShopName(food.shopId),
        所属分类: getCategoryName(food.shopFoodCategoryId),
        当前库存: food.stockQuantity ?? 0,
        状态: food.shopFoodStatus === 0 ? '上架' : '下架',
        更新时间: formatDateTime(food.modifyTime || food.createTime),
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '菜品库存');
      XLSX.writeFile(workbook, `菜品库存_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      message.success(`成功导出 ${rows.length} 条库存数据`);
    } catch (error) {
      console.error('导出菜品库存失败:', error);
      message.error('导出菜品库存失败，请稍后重试');
    } finally {
      exporting.value = false;
    }
  };

  const handleSearch = () => {
    queryParams.pageNum = 1;
    loadFoodList();
  };

  const handleReset = async () => {
    queryParams.createtimeBegin = '';
    queryParams.createtimeEnd = '';
    queryParams.pageNum = 1;
    queryParams.pageSize = 20;
    queryParams.data.shopFoodCategoryId = undefined;
    queryParams.data.shopFoodName = '';
    queryParams.data.shopFoodStatus = undefined;
    queryParams.data.searchField = '';
    dateRange.value = null;
    await loadFoodList();
  };

  const handleShopChange = async (value: number | undefined) => {
    queryParams.data.shopId = value;
    queryParams.data.shopFoodCategoryId = undefined;
    saveSelectedShop(value);
    if (!value) {
      searchCategoryOptions.value = [];
      return;
    }
    await loadCategoryListByShop(Number(value));
  };

  const handlePageChange = (page: number, pageSize: number) => {
    queryParams.pageNum = page;
    queryParams.pageSize = pageSize;
    loadFoodList();
  };

  const handlePageSizeChange = (current: number, size: number) => {
    queryParams.pageNum = current;
    queryParams.pageSize = size;
    loadFoodList();
  };

  const getShopName = (shopId?: number) => {
    const shop = shopList.value.find((item) => item.shopId === shopId);
    return shop?.shopName || '-';
  };

  const getCategoryName = (categoryId?: number) => {
    const category = categoryList.value.find((item) => item.shopFoodCategoryId === categoryId);
    return category?.categoryName || '-';
  };

  const getFoodTagNames = (tags: unknown): string[] => {
    if (typeof tags !== 'string') return [];
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag);
  };

  const getStockColor = (stockQuantity?: number) => {
    const stock = stockQuantity ?? 0;
    if (stock === 0) return 'red';
    if (stock <= 10) return 'orange';
    return 'green';
  };

  const openStockModal = async (food: FoodStockItemModel) => {
    if (!food.foodId) {
      message.warning('缺少主菜品ID');
      return;
    }
    currentStockFood.value = food;
    stockForm.stockQuantity = food.stockQuantity ?? 0;
    stockModalVisible.value = true;
    await nextTick();
    const input = document.querySelector<HTMLInputElement>('.stock-form input');
    input?.focus();
    input?.select();
  };

  const handleStockCancel = () => {
    stockModalVisible.value = false;
    stockSubmitting.value = false;
    currentStockFood.value = null;
    stockForm.stockQuantity = undefined;
  };

  const handleStockConfirm = async () => {
    if (!currentStockFood.value?.foodId) {
      message.warning('请先选择菜品');
      return;
    }
    if (stockForm.stockQuantity === undefined || stockForm.stockQuantity === null) {
      message.warning('请输入库存数量');
      return;
    }
    if (!Number.isInteger(stockForm.stockQuantity) || stockForm.stockQuantity < 0) {
      message.warning('库存数量必须为非负整数');
      return;
    }

    stockSubmitting.value = true;
    try {
      const response = await initializeFoodStock({
        stockList: [
          {
            foodId: currentStockFood.value.foodId,
            stockQuantity: stockForm.stockQuantity,
          },
        ],
      });
      if (response.code === 200) {
        message.success('库存修改成功');
        handleStockCancel();
        await loadFoodList();
      } else {
        message.error(response.msg || '库存修改失败');
      }
    } catch (error) {
      console.error('修改菜品库存异常:', error);
      message.error('修改菜品库存异常，请稍后重试');
    } finally {
      stockSubmitting.value = false;
    }
  };

  const goHome = () => {
    router.back();
  };

  onMounted(async () => {
    await loadShopList();
    await applySavedShopToQuery();
    await loadCategoryList();
    await loadFoodList();
  });

return (_ctx: any,_cache: any) => {
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_range_picker = _resolveComponent("a-range-picker")!
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_space = _resolveComponent("a-space")!
  const _component_a_table = _resolveComponent("a-table")!
  const _component_a_pagination = _resolveComponent("a-pagination")!
  const _component_a_card = _resolveComponent("a-card")!
  const _component_a_descriptions_item = _resolveComponent("a-descriptions-item")!
  const _component_a_descriptions = _resolveComponent("a-descriptions")!
  const _component_a_input_number = _resolveComponent("a-input-number")!
  const _component_a_modal = _resolveComponent("a-modal")!
  const _component_a_alert = _resolveComponent("a-alert")!
  const _component_a_config_provider = _resolveComponent("a-config-provider")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_a_config_provider, { locale: _unref(zhCN) }, {
      default: _withCtx(() => [
        _createVNode(_component_a_card, { bordered: false }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("button", {
                class: "page-back-btn",
                onClick: goHome
              }, [
                _createVNode(_unref(ArrowLeftOutlined))
              ]),
              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "page-title" }, "菜品库存管理", -1))
            ]),
            _createVNode(_component_a_form, {
              model: queryParams,
              layout: "inline",
              class: "search-form",
              onSubmit: [
                handleSearch,
                _cache[6] || (_cache[6] = _withModifiers(() => {}, ["prevent"]))
              ]
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_form_item, { label: "店铺" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: queryParams.data.shopId,
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((queryParams.data.shopId) = $event)),
                      placeholder: "请选择店铺",
                      disabled: "",
                      style: {"width":"200px"},
                      onChange: handleShopChange
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shopList.value, (shop) => {
                          return (_openBlock(), _createBlock(_component_a_select_option, {
                            key: shop.shopId,
                            value: shop.shopId
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(shop.shopName), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "分类" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: queryParams.data.shopFoodCategoryId,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((queryParams.data.shopFoodCategoryId) = $event)),
                      placeholder: "请选择分类",
                      "allow-clear": "",
                      style: {"width":"200px"},
                      disabled: !queryParams.data.shopId
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchCategoryOptions.value, (cat) => {
                          return (_openBlock(), _createBlock(_component_a_select_option, {
                            key: cat.shopFoodCategoryId,
                            value: cat.shopFoodCategoryId
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(cat.categoryName), 1)
                            ]),
                            _: 2
                          }, 1032, ["value"]))
                        }), 128))
                      ]),
                      _: 1
                    }, 8, ["value", "disabled"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "菜品名称" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: queryParams.data.shopFoodName,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((queryParams.data.shopFoodName) = $event)),
                      placeholder: "请输入菜品名称",
                      "allow-clear": "",
                      style: {"width":"200px"}
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "状态" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_select, {
                      value: queryParams.data.shopFoodStatus,
                      "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((queryParams.data.shopFoodStatus) = $event)),
                      placeholder: "请选择状态",
                      "allow-clear": "",
                      style: {"width":"120px"}
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_a_select_option, { value: 0 }, {
                          default: _withCtx(() => _cache[13] || (_cache[13] = [
                            _createTextVNode("上架", -1)
                          ])),
                          _: 1,
                          __: [13]
                        }),
                        _createVNode(_component_a_select_option, { value: 1 }, {
                          default: _withCtx(() => _cache[14] || (_cache[14] = [
                            _createTextVNode("下架", -1)
                          ])),
                          _: 1,
                          __: [14]
                        })
                      ]),
                      _: 1
                    }, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "搜索字段" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: queryParams.data.searchField,
                      "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((queryParams.data.searchField) = $event)),
                      placeholder: "支持菜品名称/描述等模糊搜索",
                      "allow-clear": "",
                      style: {"width":"260px"}
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, { label: "创建时间" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_range_picker, {
                      value: dateRange.value,
                      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((dateRange).value = $event)),
                      format: "YYYY-MM-DD",
                      style: {"width":"240px"},
                      onChange: handleDateRangeChange
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_button, {
                      type: "primary",
                      "html-type": "submit",
                      loading: loading.value
                    }, {
                      icon: _withCtx(() => [
                        _createVNode(_unref(SearchOutlined))
                      ]),
                      default: _withCtx(() => [
                        _cache[15] || (_cache[15] = _createTextVNode(" 查询 ", -1))
                      ]),
                      _: 1,
                      __: [15]
                    }, 8, ["loading"]),
                    _createVNode(_component_a_button, {
                      style: {"margin-left":"8px"},
                      onClick: handleReset
                    }, {
                      icon: _withCtx(() => [
                        _createVNode(_unref(ReloadOutlined))
                      ]),
                      default: _withCtx(() => [
                        _cache[16] || (_cache[16] = _createTextVNode(" 重置 ", -1))
                      ]),
                      _: 1,
                      __: [16]
                    }),
                    _createVNode(_component_a_button, {
                      style: {"margin-left":"8px"},
                      onClick: openImportFilePicker
                    }, {
                      icon: _withCtx(() => [
                        _createVNode(_unref(UploadOutlined))
                      ]),
                      default: _withCtx(() => [
                        _cache[17] || (_cache[17] = _createTextVNode(" Excel 导入 ", -1))
                      ]),
                      _: 1,
                      __: [17]
                    }),
                    _createVNode(_component_a_button, {
                      style: {"margin-left":"8px"},
                      loading: exporting.value,
                      onClick: handleExport
                    }, {
                      icon: _withCtx(() => [
                        _createVNode(_unref(DownloadOutlined))
                      ]),
                      default: _withCtx(() => [
                        _cache[18] || (_cache[18] = _createTextVNode(" 导出 Excel ", -1))
                      ]),
                      _: 1,
                      __: [18]
                    }, 8, ["loading"]),
                    _cache[19] || (_cache[19] = _createElementVNode("span", { class: "ml-2 text-sm text-gray-500" }, " Excel 导入库存须包含“菜品ID”和“库存数量”两列 ", -1)),
                    _createElementVNode("input", {
                      ref_key: "importFileInput",
                      ref: importFileInput,
                      type: "file",
                      accept: ".xlsx,.xls",
                      style: {"display":"none"},
                      onChange: handleImportFileChange
                    }, null, 544)
                  ]),
                  _: 1,
                  __: [19]
                })
              ]),
              _: 1
            }, 8, ["model"]),
            _createVNode(_component_a_table, {
              columns: columns,
              "data-source": tableData.value,
              loading: loading.value,
              pagination: false,
              "row-key": "shopFoodId",
              scroll: { x: 'max-content' }
            }, {
              bodyCell: _withCtx(({ column, record }) => [
                (column.key === 'shopName')
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(getShopName(record.shopId)), 1))
                  : (column.key === 'categoryName')
                    ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(getCategoryName(record.shopFoodCategoryId)), 1))
                    : (column.key === 'tags')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          (getFoodTagNames(record.tags).length > 0)
                            ? (_openBlock(), _createBlock(_component_a_space, {
                                key: 0,
                                size: [0, 4],
                                wrap: ""
                              }, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFoodTagNames(record.tags), (tag) => {
                                    return (_openBlock(), _createBlock(_component_a_tag, {
                                      key: tag,
                                      color: "blue"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(tag), 1)
                                      ]),
                                      _: 2
                                    }, 1024))
                                  }), 128))
                                ]),
                                _: 2
                              }, 1024))
                            : (_openBlock(), _createElementBlock("span", _hoisted_5, "-"))
                        ], 64))
                      : (column.key === 'stockQuantity')
                        ? (_openBlock(), _createBlock(_component_a_tag, {
                            key: 3,
                            color: getStockColor(record.stockQuantity)
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(record.stockQuantity ?? 0), 1)
                            ]),
                            _: 2
                          }, 1032, ["color"]))
                        : (column.key === 'shopFoodStatus')
                          ? (_openBlock(), _createBlock(_component_a_tag, {
                              key: 4,
                              color: record.shopFoodStatus === 0 ? 'green' : 'red'
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(record.shopFoodStatus === 0 ? '上架' : '下架'), 1)
                              ]),
                              _: 2
                            }, 1032, ["color"]))
                          : (column.key === 'modifyTime')
                            ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(formatDateTime(record.modifyTime || record.createTime)), 1))
                            : (column.key === 'action')
                              ? (_openBlock(), _createBlock(_component_a_button, {
                                  key: 6,
                                  type: "link",
                                  size: "small",
                                  onClick: ($event: any) => (openStockModal(record))
                                }, {
                                  default: _withCtx(() => _cache[20] || (_cache[20] = [
                                    _createTextVNode(" 修改库存 ", -1)
                                  ])),
                                  _: 2,
                                  __: [20]
                                }, 1032, ["onClick"]))
                              : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["data-source", "loading"]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_component_a_pagination, {
                current: queryParams.pageNum,
                "onUpdate:current": _cache[7] || (_cache[7] = ($event: any) => ((queryParams.pageNum) = $event)),
                "page-size": queryParams.pageSize,
                "onUpdate:pageSize": _cache[8] || (_cache[8] = ($event: any) => ((queryParams.pageSize) = $event)),
                total: total.value,
                "show-total": (total) => `共 ${total} 条`,
                "show-size-changer": true,
                "page-size-options": ['10', '20', '50', '100'],
                onChange: handlePageChange,
                onShowSizeChange: handlePageSizeChange
              }, null, 8, ["current", "page-size", "total", "show-total"])
            ])
          ]),
          _: 1
        }),
        _createVNode(_component_a_modal, {
          open: stockModalVisible.value,
          "onUpdate:open": _cache[10] || (_cache[10] = ($event: any) => ((stockModalVisible).value = $event)),
          title: "修改菜品库存",
          "confirm-loading": stockSubmitting.value,
          "ok-text": "保存",
          "cancel-text": "取消",
          onOk: handleStockConfirm,
          onCancel: handleStockCancel
        }, {
          default: _withCtx(() => [
            (currentStockFood.value)
              ? (_openBlock(), _createBlock(_component_a_descriptions, {
                  key: 0,
                  column: 1,
                  bordered: "",
                  size: "small"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_descriptions_item, { label: "主菜品ID" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(currentStockFood.value.foodId), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_descriptions_item, { label: "店铺菜品ID" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(currentStockFood.value.shopFoodId), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_descriptions_item, { label: "菜品名称" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(currentStockFood.value.shopFoodName || '-'), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_descriptions_item, { label: "所属店铺" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(getShopName(currentStockFood.value.shopId)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_descriptions_item, { label: "所属分类" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(getCategoryName(currentStockFood.value.shopFoodCategoryId)), 1)
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_a_descriptions_item, { label: "状态" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(currentStockFood.value.shopFoodStatus === 0 ? '上架' : '下架'), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(_component_a_form, {
              layout: "vertical",
              class: "stock-form"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_form_item, {
                  label: "库存数量",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input_number, {
                      value: stockForm.stockQuantity,
                      "onUpdate:value": _cache[9] || (_cache[9] = ($event: any) => ((stockForm.stockQuantity) = $event)),
                      min: 0,
                      precision: 0,
                      style: {"width":"100%"},
                      placeholder: "请输入库存数量"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["open", "confirm-loading"]),
        _createVNode(_component_a_modal, {
          open: importModalVisible.value,
          "onUpdate:open": _cache[11] || (_cache[11] = ($event: any) => ((importModalVisible).value = $event)),
          title: "Excel 库存导入",
          width: "720px",
          "confirm-loading": importSubmitting.value,
          "ok-button-props": { disabled: importErrors.value.length > 0 || importRows.value.length === 0 },
          "ok-text": "确认导入",
          "cancel-text": "取消",
          onOk: handleImportConfirm,
          onCancel: handleImportCancel
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_alert, {
              class: "mb-4",
              type: "info",
              "show-icon": "",
              message: "请使用固定列名“菜品ID”和“库存数量”，其他列将被忽略。"
            }),
            _createElementVNode("div", _hoisted_8, " 文件：" + _toDisplayString(importFileName.value || '-') + "，有效数据 " + _toDisplayString(importRows.value.length) + " 条 ", 1),
            (importErrors.value.length > 0)
              ? (_openBlock(), _createBlock(_component_a_alert, {
                  key: 0,
                  class: "mb-4",
                  type: "error",
                  "show-icon": "",
                  message: "文件校验失败，请修正后重新选择"
                }, {
                  description: _withCtx(() => [
                    _createElementVNode("ul", _hoisted_9, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(importErrors.value, (error) => {
                        return (_openBlock(), _createElementBlock("li", { key: error }, _toDisplayString(error), 1))
                      }), 128))
                    ])
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            (importRows.value.length > 0)
              ? (_openBlock(), _createBlock(_component_a_table, {
                  key: 1,
                  columns: importColumns,
                  "data-source": importRows.value,
                  pagination: { pageSize: 10, hideOnSinglePage: true },
                  "row-key": "foodId",
                  size: "small",
                  scroll: { y: 320 }
                }, null, 8, ["data-source"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["open", "confirm-loading", "ok-button-props"])
      ]),
      _: 1
    }, 8, ["locale"])
  ]))
}
}

})